(function () {
  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function init(sectionEl) {
    const tablist = sectionEl.querySelector('.cst__tablist');
    const panelsWrap = sectionEl.querySelector('.cst__panels');
    const tabs = Array.from(sectionEl.querySelectorAll('[role="tab"]'));
    const panels = Array.from(sectionEl.querySelectorAll('[role="tabpanel"]'));

    if (!tablist || !panelsWrap || !tabs.length || !panels.length) return;

    // Indicator inside scroll container
    let indicator = tablist.querySelector('.cst__indicator');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'cst__indicator';
      tablist.appendChild(indicator);
    }

    const DURATION = 300; // must match CSS transition duration
    let switchToken = 0;

    function getActiveTab() {
      return tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];
    }

    function getActivePanel() {
      // Active panel is the one not hidden (after our cleanup), fallback by class
      return panels.find((p) => !p.hidden) || panels.find((p) => p.classList.contains('is-active')) || panels[0];
    }

    function updateIndicator(activeTab, animate = true) {
      if (!activeTab) return;

      if (!animate) indicator.style.transition = 'none';
      indicator.style.width = `${activeTab.offsetWidth}px`;
      indicator.style.transform = `translateX(${activeTab.offsetLeft}px)`;
      if (!animate) {
        indicator.offsetHeight; // force reflow
        indicator.style.transition = 'transform 0.18s ease, width 0.18s ease';
      }
    }

    function scrollTabIntoView(tab) {
      const target = tab.offsetLeft - (tablist.clientWidth - tab.offsetWidth) / 2;
      const max = tablist.scrollWidth - tablist.clientWidth;
      tablist.scrollTo({ left: clamp(target, 0, max), behavior: 'smooth' });
    }

    function setWrapHeightToPanel(panel) {
      if (!panel) return;
      // Ensure it's measurable
      const prevHidden = panel.hidden;
      panel.hidden = false;

      // scrollHeight is reliable for tall content
      const h = panel.scrollHeight;
      panelsWrap.style.height = `${h}px`;

      panel.hidden = prevHidden;
    }

    function activate(tab, opts = { focus: false, scroll: true, animate: true }) {
      // Set selected tab
      tabs.forEach((t) => t.setAttribute('aria-selected', t === tab ? 'true' : 'false'));

      const targetPanelId = tab.getAttribute('aria-controls');
      const nextPanel = panels.find((p) => p.id === targetPanelId);
      const currentPanel = getActivePanel();

      // Indicator + tablist scroll
      updateIndicator(tab, true);
      if (opts.scroll) scrollTabIntoView(tab);
      if (opts.focus) tab.focus();

      if (!nextPanel) return;

      // If same panel, just ensure height is correct
      if (nextPanel === currentPanel) {
        setWrapHeightToPanel(nextPanel);
        return;
      }

      // Token to ignore stale timeouts if user clicks fast
      const myToken = ++switchToken;

      // BEFORE animation: hide everything except current + next
      panels.forEach((p) => {
        p.hidden = true;
        p.classList.remove('is-active', 'is-leaving');
      });

      currentPanel.hidden = false;
      nextPanel.hidden = false;

      // If no animation (init), instantly switch
      if (!opts.animate) {
        currentPanel.hidden = true;
        nextPanel.hidden = false;
        nextPanel.classList.add('is-active');
        setWrapHeightToPanel(nextPanel);
        return;
      }

      // Lock height to max of the two during transition (prevents jump)
      const maxH = Math.max(currentPanel.scrollHeight, nextPanel.scrollHeight);
      panelsWrap.style.height = `${maxH}px`;

      // Start transition:
      // - current fades out (is-leaving)
      // - next fades in (is-active)
      currentPanel.classList.add('is-active');
      currentPanel.classList.add('is-leaving');

      nextPanel.classList.add('is-active');

      // End transition cleanup
      window.setTimeout(() => {
        if (myToken !== switchToken) return;

        // Hide everything except the active (next) panel
        panels.forEach((p) => {
          p.classList.remove('is-leaving');
          p.hidden = p !== nextPanel;
        });

        // Ensure correct final classes
        currentPanel.classList.remove('is-active', 'is-leaving');
        nextPanel.classList.add('is-active');

        // Set wrapper height to active panel only (no more blank scroll)
        setWrapHeightToPanel(nextPanel);
      }, DURATION);
    }

    // Click + keyboard events
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activate(tab, { focus: false, scroll: true, animate: true }));

      tab.addEventListener('keydown', (e) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
        e.preventDefault();

        const i = tabs.indexOf(tab);
        let next = tab;

        if (e.key === 'ArrowRight') next = tabs[i + 1] || tabs[0];
        if (e.key === 'ArrowLeft') next = tabs[i - 1] || tabs[tabs.length - 1];
        if (e.key === 'Home') next = tabs[0];
        if (e.key === 'End') next = tabs[tabs.length - 1];

        activate(next, { focus: true, scroll: true, animate: true });
      });
    });

    // Keep indicator + wrapper height correct on resize/font changes
    const onResize = () => {
      updateIndicator(getActiveTab(), false);
      setWrapHeightToPanel(getActivePanel());
    };
    window.addEventListener('resize', onResize, { passive: true });

    // If content changes after load (images, dynamic content), update height
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => {
        setWrapHeightToPanel(getActivePanel());
      });
      panels.forEach((p) => ro.observe(p));
    }

    // INIT: choose the panel matching selected tab, hide others
    const firstTab = getActiveTab();
    const firstPanelId = firstTab.getAttribute('aria-controls');
    const firstPanel = panels.find((p) => p.id === firstPanelId) || panels[0];

    panels.forEach((p) => {
      p.hidden = p !== firstPanel;
      p.classList.remove('is-active', 'is-leaving');
    });
    firstPanel.classList.add('is-active');

    // Set indicator + height
    updateIndicator(firstTab, false);
    setWrapHeightToPanel(firstPanel);
  }

  function boot() {
    document.querySelectorAll('.cst[data-cst-section]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', boot);

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', function (event) {
    const sectionEl = event.target;
    if (sectionEl && sectionEl.matches('.cst[data-cst-section]')) init(sectionEl);
    else if (sectionEl) sectionEl.querySelectorAll?.('.cst[data-cst-section]').forEach(init);
  });
})();
